import { Add } from '@mui/icons-material';
import {
  Box,
  Grid,
  Typography,
  Button as MuiButton,
  FormControlLabel,
  Divider,
  ButtonGroup,
  Switch
} from '@mui/material';
import { useState } from 'react';
import { PageTitleWithBack, PageTitleWithButton } from '../../../components';
import { CodeBlock } from '../code-block';
import { handleCopyClick } from '../constants';
import { StyleGuidePage } from '../style-guide-page';

export const StyleGuidePageTitleWBack = () => {
  const [isCopied, setIsCopied] = useState<{
    [val: string]: boolean;
  }>({});
  const [showInActive, setShowInActive] = useState<boolean>(false);

  return (
    <StyleGuidePage title="Page Title w/ Back">
      <Grid container mb={2}>
        <Grid item xs={12} lg={8}>
          <Typography mb={1}>
            The page title component was built upon the Page Title component in order to render the
            page title of the page (h1) with various different configurations, but primarily to
            include a back button to the left of the title.
          </Typography>
          <Typography mb={1}>
            This component is used for most related / child pages to allow the user to navigate to
            the related / parent page.
          </Typography>
          <code>
            Props:
            <br />- backText: string
            <br />- backUrl: string
            <br />- title: string
            <br />- additionalHeaderContent?: JSX.Element
            <br />- subTitle?: string | JSX.Element
            <br />- showDivider?: boolean
            <br />- isExternalLink?: boolean
          </code>
          <Typography my={1}>
            There is a secondary version of this component that allows for action buttons to be
            displayed to the right of the page title called <em>Page Title w/ Button</em>.
          </Typography>
          <code>
            Props:
            <br />- button: JSX.Element
            <br />- pageTitleProps?: IPageTitleProps
            <br />- pageTitleBackProps?: IPageWithBackTitleProps
          </code>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Box mb={1}>
            <PageTitleWithBack
              title="Custom Page Title"
              backText="Back to Kitchen Sink"
              backUrl="/kitchen-sink"
            />
          </Box>
          <CodeBlock handleCopyClick={e => handleCopyClick(setIsCopied, e)} isCopied={isCopied}>
            &lt;PageTitleWithBack <br />
            &nbsp;&nbsp;title="Custom Page Title" <br />
            &nbsp;&nbsp;backText="Back to Kitchen Sink" <br />
            &nbsp;&nbsp;backUrl="/kitchen-sink" <br />
            /&gt;
          </CodeBlock>
        </Grid>
        <Grid item xs={12}>
          <Box mb={1}>
            <PageTitleWithButton
              button={
                <MuiButton
                  startIcon={<Add />}
                  color="primary"
                  variant="contained"
                  onClick={() => {}}
                >
                  Add A Thing
                </MuiButton>
              }
              pageTitleBackProps={{
                title: 'Page Title w/ Button',
                backText: 'Back to Kitchen Sink',
                backUrl: '/kitchen-sink'
              }}
            />
          </Box>
          <CodeBlock handleCopyClick={e => handleCopyClick(setIsCopied, e)} isCopied={isCopied}>
            &lt;PageTitleWithButton <br />
            &nbsp;&nbsp;&nbsp;button=&#123; <br />
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&lt;MuiButton <br />
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;startIcon=&#123;&lt;Add
            /&gt;&#125; <br />
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;color="primary" <br />
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;variant="contained" <br />
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;onClick=&#123;() =&gt;
            &#123;&#125;&#125;
            <br /> &nbsp;&nbsp;&nbsp;&nbsp; &gt; <br />
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Add A Thing <br />
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&lt;/MuiButton&gt; <br />
            &nbsp;&nbsp;&nbsp;&#125; <br />
            &nbsp;&nbsp;&nbsp;pageTitleBackProps=&#123;&#123; <br />
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;title: 'Custom Page Title w/ Button', <br />
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;backText: 'Back to Kitchen Sink', <br />
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;backUrl: '/kitchen-sink' <br />
            &nbsp;&nbsp;&nbsp;&#125;&#125;
            <br /> /&gt;
          </CodeBlock>
        </Grid>
        <Grid item xs={12}>
          <Box mb={1}>
            <PageTitleWithButton
              button={
                <Box display="flex">
                  <FormControlLabel
                    className="print--none"
                    control={
                      <Switch
                        name="active-inactive"
                        color="primary"
                        checked={showInActive}
                        id="active-inactive"
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          setShowInActive(e.target.checked);
                        }}
                      />
                    }
                    label="Show Inactive"
                  />
                  <Divider orientation="vertical" flexItem sx={{ margin: '0 1rem 0 0' }} />
                  <ButtonGroup
                    sx={{
                      display: 'flex',
                      flexDirection: {
                        xs: 'column',
                        sm: 'row'
                      }
                    }}
                  >
                    <MuiButton
                      startIcon={<Add />}
                      color="primary"
                      variant="contained"
                      onClick={() => {}}
                    >
                      Add Thing 1
                    </MuiButton>
                    <MuiButton
                      startIcon={<Add />}
                      color="primary"
                      variant="contained"
                      onClick={() => {}}
                    >
                      Add Thing 2
                    </MuiButton>
                  </ButtonGroup>
                </Box>
              }
              pageTitleBackProps={{
                title: 'Title w/ Many Things',
                backText: 'Back to Kitchen Sink',
                backUrl: '/kitchen-sink'
              }}
            />
          </Box>
          <CodeBlock handleCopyClick={e => handleCopyClick(setIsCopied, e)} isCopied={isCopied}>
            &lt;PageTitleWithButton <br />
            &nbsp;&nbsp;&nbsp;button=&#123; <br />
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&lt;Box display="flex"&gt; <br />
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&lt;FormControlLabel
            control=&#123;
            <br />
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&lt;Switch
            name="active-inactive" color="primary" checked=&#123;showInActive&#125;
            id="active-inactive" onChange=&#123;(e: React.ChangeEvent&lt;HTMLInputElement&gt;) =&gt;
            &#123; setShowInActive(e.target.checked); &#125;&#125; /&gt; <br />
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&#125; label="Show Inactive" /&gt;
            <br />
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&lt;Divider orientation="vertical"
            flexItem sx=&#123;&#123; margin: '0 1rem 0 0' &#125;&#125; /&gt; <br />
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&lt;ButtonGroup sx=&#123;&#123;
            display: 'flex', flexDirection: &#123; xs: 'column', sm: 'row' &#125; &#125;&#125; &gt;
            <br />
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&lt;MuiButton
            startIcon=&#123;&lt;Add /&gt;&#125; color="primary" variant="contained" onClick=&#123;()
            =&gt; &#123;&#125;&#125; &gt; Add Thing 1 &lt;/MuiButton&gt; <br />
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&lt;MuiButton
            startIcon=&#123;&lt;Add /&gt;&#125; color="primary" variant="contained" onClick=&#123;()
            =&gt; &#123;&#125;&#125; &gt; Add Thing 2 &lt;/MuiButton&gt; <br />
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&lt;/ButtonGroup&gt;
            <br />
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&lt;/Box&gt; &nbsp;&nbsp;&nbsp; <br />
            &nbsp;&nbsp;&nbsp;&#125; <br />
            &nbsp;&nbsp;&nbsp;pageTitleBackProps=&#123;&#123; <br />
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;title: 'Custom Page Title w/ Button', <br />
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;backText: 'Back to Kitchen Sink', <br />
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;backUrl: '/kitchen-sink' <br />
            &nbsp;&nbsp;&nbsp;&#125;&#125;
            <br /> /&gt;
          </CodeBlock>
        </Grid>
      </Grid>
    </StyleGuidePage>
  );
};
